import React, {useState, useEffect, useRef} from 'react'
import {motion} from 'framer-motion'
import {OpenAIService} from '../../services/openaiService'
import {SupabaseService} from '../../services/supabaseService'
import {CreditService} from '../../services/creditService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiZap, FiLoader, FiCheck, FiAlertCircle, FiRefreshCw} = FiIcons

const ContentGenerator = ({result, keyword, user, searchId, onContentGenerated, hasContent, onPurchaseClick, actionsRight}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [insufficientCredits, setInsufficientCredits] = useState(false)
  const [manualContent, setManualContent] = useState('')
  const [manualSubject, setManualSubject] = useState('')
  const [regenerating, setRegenerating] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const successTimerRef = useRef(null)

  const generateContent = async () => {
    setLoading(true)
    setError('')
    setInsufficientCredits(false)
    setRegenerating(hasContent)
    try {
      console.log('Starting content generation for result:', result.id);
      // First check if user has enough credits (0.0025) and deduct the amount
      const creditUsed = await CreditService.spendFractionalCredit(user.id, searchId)
      if (creditUsed) {
        console.log('Credit deduction failed - insufficient credits');
        setInsufficientCredits(true)
        throw new Error('Insufficient credits. You need at least 0.0025 credits to generate content.')
      }

      console.log('Credit deduction successful, generating content...');
      // Fetch user personalization data
      const userData = await SupabaseService.getUserPersonalization(user.id, searchId);

      // If credits were successfully deducted, generate content
      const content = await OpenAIService.generateContent(
        result.url,
        keyword,
        result.category,
        userData
      )

      console.log('Content generated, saving to database...');
      
      // Extract subject line if it exists (for Niche Collaborations, Guest Posts and Resource Pages)
      let subject = "No subject line detected";
      let messageBody = content;
      
      if (result.category === 'Guest Posts' || result.category === 'Resource Pages' || result.category === 'Niche Collaborations' || result.category === 'Custom Results') {
        // Look for "Subject:" at the beginning of a line (case-insensitive)
        const subjectMatch = content.match(/^Subject:\s*(.+)$/im);
        
        if (subjectMatch) {
          // Extract the subject line
          subject = subjectMatch[1].trim();
          
          // Remove the subject line from the content
          messageBody = content.replace(/^Subject:\s*.+$/im, '').trim();
        }
      }

      // Save to Supabase with subject line
      await SupabaseService.saveGeneratedContent(
        user.id,
        searchId,
        result.id,
        messageBody,
        subject
      )

      console.log('Content saved successfully');
      // Notify parent component to refresh credit balance and update content display
      if (onContentGenerated) {
        onContentGenerated(messageBody, subject)
      }

      // Temporarily show success message for 3 seconds
      setShowSuccess(true)
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
      successTimerRef.current = setTimeout(() => {
        setShowSuccess(false)
      }, 3000)
    } catch (error) {
      console.error('Content generation error:', error)
      if (!insufficientCredits) {
        setError('Failed to generate content. This action requires 0.0025 credits.')
      } else {
        setInsufficientCredits(true)
        setError('Insufficient credits. You need at least 0.0025 credits to generate content.')
      }
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }

  const saveManualContent = async () => {
    if (!manualContent.trim()) {
      setError('Please enter some content')
      return
    }

    setLoading(true)
    setError('')
    try {
      const subject = manualSubject.trim() || "No subject line detected";
      const content = manualContent.trim();

      // Save to Supabase with subject line
      await SupabaseService.saveGeneratedContent(
        user.id,
        searchId,
        result.id,
        content,
        subject
      )

      // Notify parent component to refresh content
      if (onContentGenerated) {
        onContentGenerated(content, subject)
      }

      // Reset form
      setManualContent('')
      setManualSubject('')
    } catch (error) {
      console.error('Error saving manual content:', error)
      setError('Failed to save content. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
    }
  }, [])

  // If content has already been generated, show a success message
  if (hasContent) {
    return (
      <div className="flex items-center">
        {showSuccess && (
          <div className="flex items-center text-green-600 dark:text-green-400">
            <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
            <span className="text-sm">Content generated successfully</span>
          </div>
        )}
        <button
          onClick={generateContent}
          disabled={regenerating}
          className="flex items-center ml-4 text-blue-600 hover:text-blue-800 text-sm px-1 py-1 dark:text-blue-400 dark:hover:text-blue-300 no-border"
        >
          <SafeIcon
            icon={regenerating ? FiLoader : FiRefreshCw}
            className={`w-3 h-3 mr-1 ${regenerating ? 'animate-spin' : ''}`}
          />
          {regenerating ? 'Regenerating content...' : 'Regenerate content'}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3 w-full">
      {/* For inline manual content entry */}
      {!hasContent && (
        <>
          {(result.category === 'Guest Posts' || result.category === 'Resource Pages' || result.category === 'Niche Collaborations' || result.category === 'Custom Results') && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Subject Line
              </label>
              <textarea
                value={manualSubject}
                onChange={(e) => setManualSubject(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent resize-vertical min-h-[40px] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Enter subject line..."
                rows={1}
                style={{height: 'auto'}}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = (e.target.scrollHeight) + 'px';
                }}
              />
            </div>
          )}
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Message Body
            </label>
            <textarea
              value={manualContent}
              onChange={(e) => setManualContent(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent resize-vertical min-h-[100px] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Enter your content here..."
              style={{height: 'auto'}}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = (e.target.scrollHeight) + 'px';
              }}
            />
          </div>
          
          <div className="flex items-center gap-3">
            <motion.button
              onClick={generateContent}
              disabled={loading}
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              className="inline-flex items-center px-1 py-0 sm:px-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <SafeIcon
                icon={loading ? FiLoader : insufficientCredits ? FiAlertCircle : FiZap}
                className={`hidden sm:inline w-4 h-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`}
              />
              {loading ? 'Generating content...' : 'Generate Content'}
            </motion.button>
            
            <motion.button
              onClick={saveManualContent}
              disabled={loading || !manualContent.trim()}
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              className="inline-flex items-center px-1 py-0 sm:px-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
            >
              <SafeIcon
                icon={loading ? FiLoader : FiCheck}
                className={`hidden sm:inline w-4 h-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`}
              />
              Save Content
            </motion.button>

            {/* Additional actions from parent (e.g., Send Email button) */}
            {actionsRight}
          </div>
        </>
      )}

      {loading && (
        <div className="mt-2 text-sm text-[#D59A4C] bg-[#D59A4C20] p-2 rounded">
          Please wait, your content will display in one moment...
        </div>
      )}
      
      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded dark:bg-red-900/30">
          {error}
          {insufficientCredits && (
            <span>
              {' '}You must{' '}
              <button
                onClick={onPurchaseClick}
                className="text-blue-600 hover:text-blue-800 underline font-medium no-border dark:text-blue-400 dark:hover:text-blue-300"
              >
                buy more credits
              </button>
              .
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default ContentGenerator
